import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { SearchBoxValue } from "../../../types/Form";

const SearchBox: FC<{
  value: number | string;
  onChange: (value: number | string) => void;
  isIncorrectValue?: boolean;
  labelWidth?: string;
  inputWidth?: string;
  loading?: boolean;
  options: SearchBoxValue[];
  label?: string;
  selectedValues?: SearchBoxValue[];
  hiddenButton?: boolean;
  className?: string;
  width?: string;
  defaultDisplayValue?: string;
  maxResults?: number;
}> = ({
  value,
  onChange,
  isIncorrectValue,
  labelWidth,
  inputWidth,
  label,
  options,
  loading,
  selectedValues,
  hiddenButton,
  className,
  width,
  defaultDisplayValue,
  maxResults,
}) => {
  const [filteredOptionList, setFilteredOptionList] = useState<
    SearchBoxValue[]
  >([]);
  const [query, setQuery] = useState<string>("");
  const [displayedValue, setDisplayedValue] = useState<string>("");
  const [displayedId, setDisplayedId] = useState<number | string | null>(null);

  const normalizeSearchValue = useCallback((currentValue: string) => {
    return currentValue
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }, []);

  const getOptionSearchValue = useCallback(
    (option: SearchBoxValue) => {
      return normalizeSearchValue(
        [option.name, option.searchName, option.secondaryName]
          .filter(Boolean)
          .join(" "),
      );
    },
    [normalizeSearchValue],
  );

  const getOptionSearchScore = useCallback(
    (option: SearchBoxValue, currentQuery: string) => {
      const normalizedQuery = normalizeSearchValue(currentQuery);
      const normalizedName = normalizeSearchValue(option.name);
      const normalizedSearchValue = getOptionSearchValue(option);
      const queryTokens = normalizedQuery.split(" ").filter(Boolean);

      if (!queryTokens.length) {
        return 0;
      }

      const compactQuery = normalizedQuery.replace(/\s+/g, "");
      const compactName = normalizedName.replace(/\s+/g, "");
      const compactSearchValue = normalizedSearchValue.replace(/\s+/g, "");
      const hasEveryToken = queryTokens.every((token) =>
        normalizedSearchValue.includes(token),
      );

      if (!hasEveryToken && !compactSearchValue.includes(compactQuery)) {
        return null;
      }

      if (normalizedName === normalizedQuery) {
        return 0;
      }

      if (normalizedName.startsWith(normalizedQuery)) {
        return 1;
      }

      if (compactName.includes(compactQuery)) {
        return 2;
      }

      if (queryTokens.every((token) => normalizedName.includes(token))) {
        return 3;
      }

      if (normalizedSearchValue.startsWith(normalizedQuery)) {
        return 4;
      }

      return 5;
    },
    [getOptionSearchValue, normalizeSearchValue],
  );

  useEffect(() => {
    const newFilteredOptionList = query
      ? options
          .map((option, index) => ({
            option,
            index,
            score: getOptionSearchScore(option, query),
          }))
          .filter((optionData) => optionData.score !== null)
          .sort((a, b) => {
            if (a.score !== b.score) {
              return (a.score || 0) - (b.score || 0);
            }

            return a.index - b.index;
          })
          .map((optionData) => optionData.option)
      : options;

    setFilteredOptionList(
      typeof maxResults === "number"
        ? newFilteredOptionList.slice(0, maxResults)
        : newFilteredOptionList,
    );
  }, [options, query, getOptionSearchScore, maxResults]);

  const onBeforeLeaveOptionsListHandler = useCallback(() => {
    if (filteredOptionList.length === 1) {
      onChange(filteredOptionList[0].id);
      return;
    }

    let currentDisplayedValue: SearchBoxValue | undefined = undefined;

    if (displayedId !== null) {
      currentDisplayedValue = options.find(
        (option) => option.id === displayedId,
      );
    } else if (displayedValue) {
      currentDisplayedValue = options.find(
        (option) => option.name === displayedValue,
      );
    }

    if (currentDisplayedValue) {
      onChange(currentDisplayedValue.id);
    }

    if (!query) {
      onChange("");
    }
  }, [filteredOptionList, onChange, displayedId, displayedValue, options]);

  const valueObj = useMemo(() => {
    if (!loading) {
      return options.find((option) => option.id === value);
    }
  }, [value, loading, options]);

  useEffect(() => {
    if (valueObj) {
      setDisplayedValue(valueObj.name);
      return;
    }
  }, [valueObj]);

  useEffect(() => {
    if (defaultDisplayValue && !displayedValue) {
      setDisplayedValue(defaultDisplayValue);
    }
  }, [valueObj, options, defaultDisplayValue]);

  const onBeforeEnterHandler = useCallback(() => {
    if (!!filteredOptionList.length) {
      value = filteredOptionList[0].id
    }
  }, [filteredOptionList])

  return (
    <div
      className={`search-box ${className || ""} ${
        label ? "search-box_with-label" : ""
      }`}
    >
      {label && (
        <div
          className="search-box__label"
          style={{ width: `${labelWidth || "max-content"}` }}
        >
          {label}{" "}
          {isIncorrectValue && <i className="icon-cancel-circled red" />}
        </div>
      )}
      <div style={{ width: `${inputWidth || "unset"}` }}>
        <Combobox
          value={value}
          onChange={(newCurrentValue) => onChange(newCurrentValue)}
        >
          {!!selectedValues && !!selectedValues.length && (
            <div className="search-box__selected-list">
              {" "}
              {selectedValues.map((selectedItem, index) => (
                <div
                  key={`selected-item=search-box-${selectedItem.id}-${index}`}
                  className="search-box__selected-item"
                >
                  <span className="label">{selectedItem.name}</span>
                  <button
                    onClick={() => {
                      onChange(selectedItem.id);
                    }}
                  >
                    <i className="icon-cancel" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="search-box__wrapper">
            <Combobox.Input
              onChange={(event) => {
                const value = event.currentTarget.value;
                setQuery(value);
              }}
              displayValue={() => valueObj?.name || query || ""}
              className={`search-box__input ${
                isIncorrectValue ? "search-box__input_error" : ""
              }`}
              placeholder={displayedValue || "Search..."}
            />
            {!hiddenButton && (
              <Combobox.Button>
                <div className={`btn btn-small btn-blue search-box__button`}>
                  <i className="icon-angle-down" />
                </div>
              </Combobox.Button>
            )}

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              beforeLeave={onBeforeLeaveOptionsListHandler}
              afterLeave={() => {
                setQuery("");
                setDisplayedId(null);
              }}
            >
              <Combobox.Options
                static
                className="search-box__options"
                style={{ width: width ? width : "100%" }}
              >
                {loading && (
                  <Combobox.Option
                    disabled
                    value="loading"
                    className="search-box__option"
                  >
                    Loading...
                  </Combobox.Option>
                )}
                {!loading && !filteredOptionList.length && query && (
                  <Combobox.Option
                    disabled
                    value="no-data"
                    className="search-box__option"
                  >
                    Nothing found.
                  </Combobox.Option>
                )}
                {!loading &&
                  filteredOptionList.map((option) => (
                    <Combobox.Option
                      key={option.id}
                      onMouseEnter={() => {
                        setDisplayedValue(option.name);
                        setDisplayedId(option.id);
                      }}
                      value={option.id}
                    >
                      {({ active, selected }) => (
                        <div
                          className={`search-box__option font-bold ${
                            active ? "search-box__option_active" : ""
                          }`}
                        >
                          <div className="search-box__option-name">
                            {option.name}
                          </div>
                          {!!option.secondaryName && (
                            <div className="search-box__option-secondary">
                              {option.secondaryName}
                            </div>
                          )}
                        </div>
                      )}
                    </Combobox.Option>
                  ))}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>
    </div>
  );
};

export default SearchBox;
